import { Badge, badgeClass, Button, Dialog, DialogContent, fieldClass, selectClass } from '@ledgerloom/ui';
import { useEffect, useId, useMemo, useRef, useState, type CSSProperties, type MouseEvent, type ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { api, propose } from '../api';
import { useApi, usePageTitle, usePoll, useStored } from '../hooks';
import { hasSurface, useSession } from '../session';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { Field } from '../components/Field';
import { Empty, ErrorBanner, Icon, ListSkeleton, outcomeMessage, outcomeTone, useToast } from '../components/ui';
import { PartyLink } from '../components/PartyName';

// ADR 0021: the schedules a person may see, laid out like a desk calendar
// (pattern data-display/calendar): a rail with Create, a month to jump by, and
// the calendars to show; a Day, Week, or Month grid, or a Schedule list for
// keyboard, screen reader, and phone; the day plan in a side panel
// (data-display/timeline); event details and a new event in modals
// (content-management/modal). The view, date, and whose plan are in the URL.

interface Ref { type: string; id: string }
interface Action { kind: string; title: string; why: string; ref: Ref; due_at: string | null; minutes: number }
interface AgendaEvent { id: string; title: string; starts_at: string; ends_at: string; kind: string; location: string | null; calendar: 'user' | 'workspace'; party: { id: string; name: string } | null; deal: { id: string; title: string; stage: string } | null }
interface Block { id: string; kind: 'event' | 'free'; starts_at: string; ends_at: string; event: AgendaEvent | null; prepare: Action[]; follow_up: Action[]; suggested: Action[] }
interface Agenda {
  date: string; timezone: string; person: { user_id: string; display_name: string };
  now: { block_id: string | null; headline: string; next_action: Action | null };
  all_day: AgendaEvent[]; blocks: Block[]; backlog: Action[];
  counts: { meetings: number; overdue: number; due_today: number; waiting: number };
}
interface Cal { id: string; kind: 'user' | 'workspace'; owner_user_id: string | null; name: string; level: 'details' | 'busy'; mine: boolean }
interface Ev { id: string; calendar_id: string; title: string; starts_at: string; ends_at: string; all_day: boolean; busy_only: boolean; location: string | null; party_id: string | null; party_name: string | null; deal_id: string | null; deal_title: string | null }
// A calendar as drawn: its colour slot (1 to 6) and the name it shows under.
interface Shown extends Cal { colour: number; label: string }

type View = 'day' | 'week' | 'month' | 'schedule';
const VIEWS: Array<[View, string, string]> = [['day', 'Day', 'd'], ['week', 'Week', 'w'], ['month', 'Month', 'm'], ['schedule', 'Schedule', 'a']];
// Links from before the grid: the day plan was "today", the master view "everyone".
const LEGACY: Record<string, View> = { today: 'day', everyone: 'week' };
const SCHEDULE_DAYS = 28;
const HOUR = 48; // pixels per hour in the time grid

const VERB: Record<string, string> = { approve: 'Decide', resolve: 'Resolve', task: 'Do', prepare: 'Prepare', follow_up: 'Follow up' };

export function hrefFor(ref: Ref): string | null {
  switch (ref.type) {
    case 'approval': case 'exception': return `/desk/needs-you?item=${encodeURIComponent(`${ref.type}:${ref.id}`)}`;
    case 'thread': return `/mail?thread=${ref.id}`;
    case 'party': return `/contacts/${ref.id}`;
    case 'deal': return `/deals/${ref.id}`;
    case 'quote': return `/quotes/${ref.id}`;
    case 'agent': return `/agents/${encodeURIComponent(ref.id)}`;
    case 'person': return `/team/people/${ref.id}`;
    default: return null;
  }
}

const clock = (iso: string, tz?: string) => new Date(iso).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', timeZone: tz });
// "10 AM", "10:30 AM": the minutes only when there are some.
const short = (d: Date) => d.toLocaleTimeString([], { hour: 'numeric', minute: d.getMinutes() ? '2-digit' : undefined });
const span = (e: Ev) => `${short(new Date(e.starts_at))} – ${short(new Date(e.ends_at))}`;
const parse = (day: string) => new Date(`${day}T12:00:00`);
const dayName = (day: string) => parse(day).toLocaleDateString([], { weekday: 'long', month: 'long', day: 'numeric' });
const ymd = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const shift = (date: string, days: number) => { const d = parse(date); d.setDate(d.getDate() + days); return ymd(d); };
const shiftMonth = (date: string, n: number) => { const d = parse(date); return ymd(new Date(d.getFullYear(), d.getMonth() + n, 1, 12)); };
const startOfWeek = (date: string) => shift(date, -parse(date).getDay());
const hhmm = (minutes: number) => `${String(Math.floor(minutes / 60)).padStart(2, '0')}:${String(minutes % 60).padStart(2, '0')}`;
const zone = () => new Intl.DateTimeFormat([], { timeZoneName: 'short' }).formatToParts(new Date()).find((p) => p.type === 'timeZoneName')?.value ?? '';

// The days a view draws and the instants it fetches.
function rangeOf(view: View, date: string): { days: string[]; from: string; to: string } {
  const run = (from: string, n: number) => ({ days: Array.from({ length: n }, (_, i) => shift(from, i)), from, to: shift(from, n) });
  if (view === 'day') return run(date, 1);
  if (view === 'week') return run(startOfWeek(date), 7);
  if (view === 'month') return run(startOfWeek(shiftMonth(date, 0)), 42);
  return run(date, SCHEDULE_DAYS);
}

function titleOf(view: View, date: string, days: string[]): string {
  const d = parse(date);
  if (view === 'day') return d.toLocaleDateString([], { month: 'long', day: 'numeric', year: 'numeric' });
  if (view === 'month') return d.toLocaleDateString([], { month: 'long', year: 'numeric' });
  const a = parse(days[0]!), b = parse(days[days.length - 1]!);
  if (a.getMonth() === b.getMonth()) return a.toLocaleDateString([], { month: 'long', year: 'numeric' });
  if (a.getFullYear() === b.getFullYear()) return `${a.toLocaleDateString([], { month: 'short' })} – ${b.toLocaleDateString([], { month: 'short', year: 'numeric' })}`;
  return `${a.toLocaleDateString([], { month: 'short', year: 'numeric' })} – ${b.toLocaleDateString([], { month: 'short', year: 'numeric' })}`;
}

const step = (view: View, date: string, dir: 1 | -1) =>
  view === 'month' ? shiftMonth(date, dir) : shift(date, dir * (view === 'day' ? 1 : view === 'week' ? 7 : SCHEDULE_DAYS));

// Which local days an event covers. An all-day event is stored as whole UTC days.
function daysOf(e: Ev): string[] {
  const out: string[] = [];
  if (e.all_day) {
    let d = e.starts_at.slice(0, 10);
    const last = new Date(new Date(e.ends_at).getTime() - 1).toISOString().slice(0, 10);
    while (d <= last && out.length < 62) { out.push(d); d = shift(d, 1); }
    return out.length ? out : [e.starts_at.slice(0, 10)];
  }
  let d = ymd(new Date(e.starts_at));
  const last = ymd(new Date(new Date(e.ends_at).getTime() - 1));
  while (d <= last && out.length < 62) { out.push(d); d = shift(d, 1); }
  return out.length ? out : [ymd(new Date(e.starts_at))];
}

// Timed events in one day column, side by side where they overlap.
interface Placed { e: Ev; top: number; height: number; lane: number; lanes: number }
function place(day: string, evs: Ev[]): Placed[] {
  const open = new Date(`${day}T00:00:00`).getTime();
  const close = new Date(`${shift(day, 1)}T00:00:00`).getTime();
  const items = evs
    .map((e) => ({ e, start: Math.max(new Date(e.starts_at).getTime(), open), end: Math.min(new Date(e.ends_at).getTime(), close) }))
    .filter((x) => x.end > x.start)
    .map((x) => ({ ...x, seen: Math.max(x.end, x.start + 20 * 60_000) })) // a short event still takes room
    .sort((a, b) => a.start - b.start || b.end - a.end);
  const out: Placed[] = [];
  let cluster: Placed[] = [], lanes: number[] = [], until = -Infinity;
  const flush = () => { for (const p of cluster) p.lanes = lanes.length; out.push(...cluster); cluster = []; lanes = []; };
  for (const x of items) {
    if (x.start >= until) flush();
    let lane = lanes.findIndex((end) => end <= x.start);
    if (lane < 0) { lane = lanes.length; lanes.push(x.seen); } else lanes[lane] = x.seen;
    until = Math.max(until, x.seen);
    const top = ((x.start - open) / 3_600_000) * HOUR;
    cluster.push({ e: x.e, top, height: Math.max(((x.end - x.start) / 3_600_000) * HOUR, 18), lane, lanes: 0 });
  }
  flush();
  return out;
}

function useNow() {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => { const t = setInterval(() => setNow(new Date()), 60_000); return () => clearInterval(t); }, []);
  return now;
}

const narrow = () => typeof window !== 'undefined' && window.matchMedia?.('(max-width: 820px)').matches;

export function Calendar() {
  usePageTitle('Calendar');
  const { me } = useSession();
  const [params, setParams] = useSearchParams();
  const raw = params.get('view') ?? '';
  const view: View = VIEWS.some(([v]) => v === raw) ? raw as View : LEGACY[raw] ?? (narrow() ? 'schedule' : 'week');
  const today = ymd(new Date());
  const date = params.get('date') ?? today;
  const person = params.get('person');
  const set = (patch: Record<string, string | null>) => {
    const next = new URLSearchParams(params);
    for (const [k, v] of Object.entries(patch)) if (v === null) next.delete(k); else next.set(k, v);
    setParams(next, { replace: true });
  };
  const cals = useApi<Cal[]>('/v1/calendars');
  const [hidden, setHidden] = useStored<string[]>('ll-calendar-hidden', []);
  // The plan sits beside the Day view until the person opens or closes it themselves.
  const [planChoice, setPlanOpen] = useStored<boolean | null>('ll-calendar-plan', null);
  const planOpen = planChoice ?? view === 'day';
  const [adding, setAdding] = useState<{ day: string; start: string; end: string } | null>(null);
  const [opened, setOpened] = useState<Ev | null>(null);
  const [version, setVersion] = useState(0);

  const shown = useMemo<Shown[]>(() => {
    const list = cals.data ?? [];
    const order = (c: Cal) => (c.mine ? 0 : c.kind === 'workspace' ? 1 : 2);
    return [...list].sort((a, b) => order(a) - order(b) || a.name.localeCompare(b.name))
      .map((c, i) => ({ ...c, colour: (i % 6) + 1, label: c.kind === 'workspace' ? `${c.name} (workspace)` : c.mine ? `${c.name} (you)` : c.name }));
  }, [cals.data]);
  const byId = useMemo(() => new Map(shown.map((c) => [c.id, c])), [shown]);

  const { days, from, to } = rangeOf(view, date);
  const q = new URLSearchParams({ from: new Date(`${from}T00:00:00`).toISOString(), to: new Date(`${to}T00:00:00`).toISOString() });
  const evs = useApi<Ev[]>(`/v1/calendar/events?${q}`, [version]);
  const visible = (evs.data ?? []).filter((e) => byId.has(e.calendar_id) && !hidden.includes(e.calendar_id));

  const create = (day = date, startMin = 10 * 60) => setAdding({ day, start: hhmm(startMin), end: hhmm(Math.min(startMin + 60, 23 * 60 + 59)) });
  const go = (patch: Record<string, string | null>) => set(patch);

  // The desk-calendar keys: t today, d w m a views, j or n next, k or p back, c create.
  const keys = useRef<(e: KeyboardEvent) => void>(() => {});
  keys.current = (e) => {
    if (e.metaKey || e.ctrlKey || e.altKey || adding || opened) return;
    const t = e.target as HTMLElement | null;
    if (t && (t.closest('input, select, textarea, [contenteditable="true"], dialog'))) return;
    const v = VIEWS.find(([, , k]) => k === e.key);
    if (v) go({ view: v[0] });
    else if (e.key === 't') go({ date: null });
    else if (e.key === 'j' || e.key === 'n') go({ date: step(view, date, 1) });
    else if (e.key === 'k' || e.key === 'p') go({ date: step(view, date, -1) });
    else if (e.key === 'c') create();
    else return;
    e.preventDefault();
  };
  useEffect(() => { const h = (e: KeyboardEvent) => keys.current(e); window.addEventListener('keydown', h); return () => window.removeEventListener('keydown', h); }, []);

  const unit = view === 'day' ? 'day' : view === 'month' ? 'month' : view === 'week' ? 'week' : 'four weeks';
  return (
    <div className={`gcal${planOpen ? ' plan-open' : ''}`}>
      <div className="gcal-bar">
        <Button type="submit" variant="secondary" className="gcal-bar-create" onClick={() => create()}><Icon name="plus" />Create</Button>
        <Button type="submit" variant="secondary" size="sm" onClick={() => go({ date: null })} title="Today (t)">Today</Button>
        <div className="row" style={{ gap: 0 }}>
          <Button type="submit" variant="ghost" size="sm" className="gcal-arrow" onClick={() => go({ date: step(view, date, -1) })} aria-label={`Previous ${unit}`} title={`Previous ${unit} (k)`}><Icon name="back" /></Button>
          <Button type="submit" variant="ghost" size="sm" className="gcal-arrow" onClick={() => go({ date: step(view, date, 1) })} aria-label={`Next ${unit}`} title={`Next ${unit} (j)`}><Icon name="chevron" /></Button>
        </div>
        <h1 className="gcal-title"><span className="sr-only">Calendar, </span>{titleOf(view, date, days)}</h1>
        <div className="gcal-bar-end">
          <label className="sr-only" htmlFor="gcal-view">View</label>
          <select id="gcal-view" className={`${selectClass} h-9 w-auto gcal-view`} value={view} onChange={(e) => go({ view: e.target.value })}>
            {VIEWS.map(([v, label, k]) => <option key={v} value={v}>{label} ({k.toUpperCase()})</option>)}
          </select>
          <Button variant="secondary" size="sm" className={planOpen ? 'border-accent bg-accent-subtle' : undefined} aria-pressed={planOpen} onClick={() => setPlanOpen(!planOpen)} title="Show what to do with the day"><Icon name="panel" />Day plan</Button>
        </div>
      </div>

      <div className="gcal-body">
        <aside className="gcal-rail" aria-label="Calendars">
          <Button type="submit" variant="secondary" className="gcal-create" onClick={() => create()}><Icon name="plus" />Create</Button>
          <MiniMonth date={date} today={today} onPick={(d) => go({ date: d === today ? null : d })} />
          {cals.error ? <ErrorBanner error={cals.error} /> : (
            <>
              <CalList title="My calendars" cals={shown.filter((c) => c.mine || c.kind === 'workspace')} hidden={hidden} setHidden={setHidden} />
              <CalList title="Other calendars" cals={shown.filter((c) => !c.mine && c.kind !== 'workspace')} hidden={hidden} setHidden={setHidden}
                empty="Nobody else shares a calendar with you yet. Ask a teammate to share theirs in Settings." />
            </>
          )}
        </aside>

        <section className="gcal-main" aria-label={titleOf(view, date, days)}>
          {evs.error ? <ErrorBanner error={evs.error} />
            : view === 'month' ? <MonthGrid date={date} today={today} days={days} evs={visible} byId={byId} onDay={(d) => go({ view: 'day', date: d })} onOpen={setOpened} onCreate={(d) => create(d)} />
            : view === 'schedule' ? (evs.data ? <Schedule days={days} today={today} evs={visible} byId={byId} onOpen={setOpened} onDay={(d) => go({ view: 'day', date: d })} /> : <ListSkeleton rows={5} label="Loading the schedule" />)
            : <TimeGrid days={days} today={today} evs={visible} byId={byId} onDay={(d) => go({ view: 'day', date: d })} onOpen={setOpened} onCreate={create} />}
        </section>

        {planOpen && (
          <aside className="gcal-plan" aria-labelledby="plan-h" tabIndex={0}>
            <div className="row" style={{ justifyContent: 'space-between' }}>
              <h2 id="plan-h">Day plan</h2>
              <Button type="submit" variant="ghost" size="sm" className="gcal-arrow" onClick={() => setPlanOpen(false)} aria-label="Close the day plan"><Icon name="x" /></Button>
            </div>
            <p className="small muted" style={{ margin: 0 }}>{dayName(date)}</p>
            {shown.filter((c) => c.kind === 'user' && c.level === 'details').length > 1 && (
              <Field label="Whose plan">
                <select className={`${selectClass} h-9 w-auto`} value={person ?? ''} onChange={(e) => go({ person: e.target.value || null })}>
                  <option value="">Mine</option>
                  {shown.filter((c) => c.kind === 'user' && c.level === 'details' && !c.mine).map((c) => <option key={c.id} value={c.owner_user_id!}>{c.name}</option>)}
                </select>
              </Field>
            )}
            {hasSurface(me, 'day_plan') && !person && <PlannerAdvice key={`advice-${date}`} date={date} collapsible />}
            <DayPlan key={`${date}-${person}-${version}`} date={date} person={person} />
          </aside>
        )}
      </div>

      {opened && <EventDialog e={opened} cal={byId.get(opened.calendar_id)} onClose={() => setOpened(null)} />}
      {adding && <NewEvent initial={adding} cals={cals.data ?? []} onClose={() => setAdding(null)} onDone={() => { setAdding(null); setVersion((n) => n + 1); }} />}
    </div>
  );
}

function MiniMonth({ date, today, onPick }: { date: string; today: string; onPick: (d: string) => void }) {
  const [month, setMonth] = useState(() => shiftMonth(date, 0));
  useEffect(() => setMonth(shiftMonth(date, 0)), [date]);
  const first = parse(month);
  const days = Array.from({ length: 42 }, (_, i) => shift(startOfWeek(month), i));
  const weekdays = days.slice(0, 7).map((d) => parse(d));
  const caption = first.toLocaleDateString([], { month: 'long', year: 'numeric' });
  return (
    <div className="gcal-mini">
      <div className="row gcal-mini-head">
        <span className="grow" aria-hidden="true">{caption}</span>
        <Button type="submit" variant="ghost" size="sm" className="gcal-arrow" onClick={() => setMonth(shiftMonth(month, -1))} aria-label="Previous month"><Icon name="back" /></Button>
        <Button type="submit" variant="ghost" size="sm" className="gcal-arrow" onClick={() => setMonth(shiftMonth(month, 1))} aria-label="Next month"><Icon name="chevron" /></Button>
      </div>
      <table data-ui="calendar-grid">
        <caption className="sr-only">{caption}</caption>
        <thead><tr>{weekdays.map((d) => <th key={d.getDay()} scope="col" abbr={d.toLocaleDateString([], { weekday: 'long' })}>{d.toLocaleDateString([], { weekday: 'narrow' })}</th>)}</tr></thead>
        <tbody>
          {[0, 1, 2, 3, 4, 5].map((w) => (
            <tr key={w}>
              {days.slice(w * 7, w * 7 + 7).map((d) => (
                <td key={d}>
                  <button className={`gcal-mini-day${d === today ? ' today' : ''}${d === date ? ' on' : ''}${parse(d).getMonth() !== first.getMonth() ? ' out' : ''}`}
                    onClick={() => onPick(d)} aria-label={dayName(d)} aria-current={d === today ? 'date' : undefined}>{parse(d).getDate()}</button>
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function CalList({ title, cals, hidden, setHidden, empty }: { title: string; cals: Shown[]; hidden: string[]; setHidden: (v: string[]) => void; empty?: string }) {
  const id = useId();
  if (!cals.length && !empty) return null;
  return (
    <div className="gcal-cals">
      <h2 id={id}>{title}</h2>
      {cals.length ? (
        <ul aria-labelledby={id}>
          {cals.map((c) => (
            <li key={c.id}>
              <label className={`cal-${c.colour}`}>
                <input type="checkbox" checked={!hidden.includes(c.id)} onChange={(e) => setHidden(e.target.checked ? hidden.filter((h) => h !== c.id) : [...hidden, c.id])} />
                <span className="grow">{c.label}</span>
                {c.level === 'busy' && <Badge>Busy only</Badge>}
              </label>
            </li>
          ))}
        </ul>
      ) : <p className="small muted" style={{ margin: 0 }}>{empty}</p>}
    </div>
  );
}

function EventChip({ e, cal, onOpen, children, className = '', style }: { e: Ev; cal?: Shown; onOpen: (e: Ev) => void; children: ReactNode; className?: string; style?: CSSProperties }) {
  return (
    <button type="button" className={`gcal-ev cal-${cal?.colour ?? 1}${e.busy_only ? ' busy' : ''} ${className}`} style={style}
      onClick={(x) => { x.stopPropagation(); onOpen(e); }}>
      {children}
      {cal && <span className="sr-only">, {cal.label}</span>}
    </button>
  );
}

function TimeGrid({ days, today, evs, byId, onDay, onOpen, onCreate }: {
  days: string[]; today: string; evs: Ev[]; byId: Map<string, Shown>;
  onDay: (d: string) => void; onOpen: (e: Ev) => void; onCreate: (day: string, startMin: number) => void;
}) {
  const now = useNow();
  const scroller = useRef<HTMLDivElement>(null);
  const showsToday = days.includes(today);
  useEffect(() => {
    const at = showsToday ? Math.max(0, now.getHours() + now.getMinutes() / 60 - 1.5) : 7.5;
    if (scroller.current) scroller.current.scrollTop = at * HOUR;
  }, [days[0], days.length]); // eslint-disable-line react-hooks/exhaustive-deps
  const allDay = (d: string) => evs.filter((e) => e.all_day && daysOf(e).includes(d));
  const anyAllDay = days.some((d) => allDay(d).length);
  const clickSlot = (day: string) => (x: MouseEvent<HTMLDivElement>) => {
    if ((x.target as HTMLElement).closest('button')) return;
    const y = x.clientY - x.currentTarget.getBoundingClientRect().top;
    onCreate(day, Math.min(23 * 60 + 30, Math.max(0, Math.floor((y / HOUR) * 2) * 30)));
  };
  const cols = { gridTemplateColumns: `56px repeat(${days.length}, minmax(0, 1fr))` };
  return (
    <div className={`gcal-grid${days.length === 1 ? ' one' : ''}`}>
      <div className="gcal-head" style={cols}>
        <div className="gcal-zone">{zone()}</div>
        {days.map((d) => (
          <div key={d} className="gcal-dayhead">
            <span className="dow" aria-hidden="true">{parse(d).toLocaleDateString([], { weekday: 'short' })}</span>
            <button className={`num${d === today ? ' today' : ''}`} onClick={() => onDay(d)} aria-label={dayName(d)} aria-current={d === today ? 'date' : undefined}>{parse(d).getDate()}</button>
          </div>
        ))}
      </div>
      {anyAllDay && (
        <div className="gcal-allday" style={cols}>
          <div className="gcal-zone">All day</div>
          {days.map((d) => (
            <div key={d} className="gcal-allday-cell">
              {allDay(d).map((e) => <EventChip key={e.id} e={e} cal={byId.get(e.calendar_id)} onOpen={onOpen} className="flat"><span className="gcal-ev-title">{e.title}</span></EventChip>)}
            </div>
          ))}
        </div>
      )}
      <div className="gcal-scroll" ref={scroller} tabIndex={0} aria-label="Times of day">
        <div className="gcal-hours" style={{ ...cols, height: 24 * HOUR }}>
          <div className="gcal-gutter" aria-hidden="true">
            {Array.from({ length: 23 }, (_, h) => <span key={h} style={{ top: (h + 1) * HOUR }}>{short(new Date(2000, 0, 1, h + 1))}</span>)}
          </div>
          {days.map((d) => {
            const timed = place(d, evs.filter((e) => !e.all_day));
            return (
              <div key={d} className="gcal-col" role="group" aria-label={dayName(d)} onClick={clickSlot(d)}>
                {timed.map(({ e, top, height, lane, lanes }) => (
                  <EventChip key={`${e.id}-${d}`} e={e} cal={byId.get(e.calendar_id)} onOpen={onOpen} className={height < 36 ? 'tight' : ''}
                    style={{ top, height: height - 2, left: `calc(${(lane / lanes) * 100}% + 2px)`, width: `calc(${100 / lanes}% - 6px)` }}>
                    <span className="gcal-ev-title">{e.title}</span>
                    <span className="gcal-ev-time">{span(e)}</span>
                  </EventChip>
                ))}
                {d === today && <div className="gcal-now" style={{ top: (now.getHours() + now.getMinutes() / 60) * HOUR }} aria-hidden="true" />}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function MonthGrid({ date, today, days, evs, byId, onDay, onOpen, onCreate }: {
  date: string; today: string; days: string[]; evs: Ev[]; byId: Map<string, Shown>;
  onDay: (d: string) => void; onOpen: (e: Ev) => void; onCreate: (d: string) => void;
}) {
  const month = parse(date).getMonth();
  const MAX = 3;
  return (
    <div className="gcal-month">
      <div className="gcal-month-dow" aria-hidden="true">{days.slice(0, 7).map((d) => <span key={d}>{parse(d).toLocaleDateString([], { weekday: 'short' })}</span>)}</div>
      <div className="gcal-month-cells">
        {days.map((d) => {
          const here = evs.filter((e) => daysOf(e).includes(d)).sort((a, b) => Number(b.all_day) - Number(a.all_day) || a.starts_at.localeCompare(b.starts_at));
          const more = here.length - MAX;
          return (
            <div key={d} className={`gcal-cell${parse(d).getMonth() !== month ? ' out' : ''}`} role="group" aria-label={dayName(d)}
              onClick={(x) => { if (!(x.target as HTMLElement).closest('button')) onCreate(d); }}>
              <button className={`num${d === today ? ' today' : ''}`} onClick={() => onDay(d)} aria-label={dayName(d)} aria-current={d === today ? 'date' : undefined}>
                {parse(d).getDate() === 1 ? parse(d).toLocaleDateString([], { month: 'short', day: 'numeric' }) : parse(d).getDate()}
              </button>
              {here.slice(0, more > 0 ? MAX - 1 : MAX).map((e) => (
                <EventChip key={e.id} e={e} cal={byId.get(e.calendar_id)} onOpen={onOpen} className={e.all_day ? 'flat' : 'dot'}>
                  {!e.all_day && <span className="gcal-ev-time">{short(new Date(e.starts_at))} </span>}
                  <span className="gcal-ev-title">{e.title}</span>
                </EventChip>
              ))}
              {more > 0 && <button className="gcal-more" onClick={() => onDay(d)}>{more + 1} more</button>}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function Schedule({ days, today, evs, byId, onOpen, onDay }: { days: string[]; today: string; evs: Ev[]; byId: Map<string, Shown>; onOpen: (e: Ev) => void; onDay: (d: string) => void }) {
  const rows = days.map((d) => ({ d, list: evs.filter((e) => daysOf(e).includes(d)).sort((a, b) => Number(b.all_day) - Number(a.all_day) || a.starts_at.localeCompare(b.starts_at)) })).filter((r) => r.list.length);
  if (!rows.length) return <Empty title="Nothing scheduled">No events in the next four weeks on the calendars you show.</Empty>;
  return (
    <ol className="gcal-sched">
      {rows.map(({ d, list }) => (
        <li key={d}>
          <div className="gcal-sched-day">
            <button className={`num${d === today ? ' today' : ''}`} onClick={() => onDay(d)} aria-label={dayName(d)} aria-current={d === today ? 'date' : undefined}>{parse(d).getDate()}</button>
            <span aria-hidden="true">{parse(d).toLocaleDateString([], { month: 'short', weekday: 'short' })}</span>
          </div>
          <ul>
            {list.map((e) => (
              <li key={e.id}>
                <EventChip e={e} cal={byId.get(e.calendar_id)} onOpen={onOpen} className="dot row-ev">
                  <span className="gcal-ev-time">{e.all_day ? 'All day' : span(e)}</span>
                  <span className="gcal-ev-title">{e.title}</span>
                </EventChip>
              </li>
            ))}
          </ul>
        </li>
      ))}
    </ol>
  );
}

// An event's facts in the library's dialog; Escape, a click outside, or Close shut it, and focus goes back to the event.
function EventDialog({ e, cal, onClose }: { e: Ev; cal?: Shown; onClose: () => void }) {
  const opener = useRef<HTMLElement | null>(null);
  const days = daysOf(e);
  const when = e.all_day
    ? (days.length > 1 ? `${dayName(days[0]!)} – ${dayName(days[days.length - 1]!)}` : `${dayName(days[0]!)} · All day`)
    : `${dayName(ymd(new Date(e.starts_at)))} · ${span(e)}`;
  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent title={e.title} className="gcal-dialog"
        onOpenAutoFocus={() => { opener.current = document.activeElement as HTMLElement | null; }}
        onCloseAutoFocus={(x) => { x.preventDefault(); opener.current?.focus(); }}>
      <dl className="gcal-facts">
        {cal && <><dt>Calendar</dt><dd><span className={`gcal-swatch cal-${cal.colour}`} aria-hidden="true" /> {cal.label}</dd></>}
        <dt>When</dt><dd>{when}</dd>
        {e.location && <><dt>Where</dt><dd>{e.location}</dd></>}
        {e.party_name && <><dt>With</dt><dd><PartyLink id={e.party_id} name={e.party_name} /></dd></>}
        {e.deal_title && <><dt>Deal</dt><dd><Link to={`/deals/${e.deal_id}`}>{e.deal_title}</Link></dd></>}
      </dl>
      {e.busy_only && <p className="small muted" style={{ margin: 0 }}>Only that this time is busy is shared with you.</p>}
      </DialogContent>
    </Dialog>
  );
}

function ActionItem({ a }: { a: Action }) {
  const href = hrefFor(a.ref);
  const title = <><Badge tone="accent" style={{ marginRight: 8 }}>{VERB[a.kind] ?? a.kind}</Badge>{a.title}</>;
  return (
    <li className="list-item" style={{ alignItems: 'flex-start', padding: '8px 0' }}>
      <div className="grow">
        <div className="item-title" style={{ whiteSpace: 'normal' }}>{href ? <Link to={href}>{title}</Link> : title}</div>
        <div className="item-sub">{a.why} · about {a.minutes} min</div>
      </div>
    </li>
  );
}

function Actions({ label, items }: { label: string; items: Action[] }) {
  if (!items.length) return null;
  return (
    <div className="stack" style={{ gap: 2 }}>
      <h5 className="small muted" style={{ margin: 0 }}>{label}</h5>
      <ul className="list" style={{ margin: 0, padding: 0, listStyle: 'none' }}>{items.map((a, i) => <ActionItem key={`${a.ref.id}-${i}`} a={a} />)}</ul>
    </div>
  );
}

interface PlannerAnswer {
  agent: { id: string; status: 'draft' | 'shadow' | 'active' | 'suspended' } | null;
  plan: { id: string; date: string; summary: string; suggestions: Array<{ title: string; why: string; ref: Ref | null }>; updated_at: string } | null;
}

// The day planner's plan for you (GET /v1/day-plan): beside the calendar, where it folds away
// (content-management/expandable-text), and on Home. Its words come from an agent and show as text.
export function PlannerAdvice({ date, collapsible = false }: { date?: string; collapsible?: boolean }) {
  const r = useApi<PlannerAnswer>(`/v1/day-plan${date ? `?date=${date}` : ''}`);
  const [open, setOpen] = useStored('ll-calendar-advice', true);
  const id = useId();
  // Asked to plan now: poll until the plan changes from the one shown when asked, for up to two minutes.
  const [asked, setAsked] = useState<{ stamp: string; at: number } | null>(null);
  const [askError, setAskError] = useState<Error | null>(null);
  const stamp = r.data?.plan?.updated_at ?? '';
  const planning = !!asked && asked.stamp === stamp && Date.now() - asked.at < 120_000;
  usePoll(planning, () => void r.reload());
  if (r.error) return <ErrorBanner error={r.error} />;
  if (!r.data?.agent) return null;
  const { agent, plan } = r.data;
  const planNow = async () => {
    setAskError(null);
    try { await api('/v1/day-plan', { method: 'POST' }); setAsked({ stamp, at: Date.now() }); } catch (e) { setAskError(e as Error); }
  };
  const today = !date || date === ymd(new Date());
  return (
    <section className="stack planner-advice" style={{ gap: 6 }} aria-labelledby={`${id}-h`}>
      <div className="row" style={{ justifyContent: 'space-between' }}>
        <h3 id={`${id}-h`}>From your day planner</h3>
        {collapsible && (
          <Button type="button" variant="ghost" size="sm" className="disclosure self-start pl-0" aria-expanded={open} aria-controls={id} onClick={() => setOpen(!open)}>{open ? 'Hide' : 'Show'}</Button>
        )}
      </div>
      <div id={id} hidden={collapsible && !open} className="stack" style={{ gap: 8 }}>
        {agent.status !== 'active' ? (
          <p className="small muted" style={{ margin: 0 }}>Your day planner is not turned on yet. Once it is, it plans each weekday morning. <Link to={`/agents/${encodeURIComponent(agent.id)}`}>Open your day planner</Link></p>
        ) : !plan ? (
          <p className="small muted" style={{ margin: 0 }}>No plan for this day yet. Your day planner plans each weekday morning.</p>
        ) : (
          <>
            <p style={{ margin: 0 }}>{plan.summary}</p>
            {plan.suggestions.length > 0 && (
              <ul className="planner-suggestions" aria-label="Suggestions">
                {plan.suggestions.map((s, i) => {
                  const href = s.ref ? hrefFor(s.ref) : null;
                  return <li key={i}><strong>{href ? <Link to={href}>{s.title}</Link> : s.title}</strong><div className="small muted">{s.why}</div></li>;
                })}
              </ul>
            )}
          </>
        )}
        {agent.status === 'active' && today && (
          <div className="row" style={{ gap: 8 }}>
            <Button type="button" variant="secondary" size="sm" onClick={() => void planNow()} disabled={planning}>{plan ? 'Plan again' : 'Plan my day now'}</Button>
            {planning && <span className="small muted" role="status">Planning your day…</span>}
          </div>
        )}
        <ErrorBanner error={askError} />
      </div>
    </section>
  );
}

function DayPlan({ date, person }: { date: string; person: string | null }) {
  const { me } = useSession();
  const q = new URLSearchParams({ date, ...(person ? { user_id: person } : {}) });
  const a = useApi<Agenda>(`/v1/agenda?${q}`);
  if (a.error) return <ErrorBanner error={a.error} />;
  if (!a.data) return <ListSkeleton rows={4} label="Planning the day" />;
  const d = a.data;
  const tz = d.timezone;
  const mine = d.person.user_id === me.user_id;
  const next = d.now.next_action;
  return (
    <div className="stack" style={{ gap: 14 }}>
      <section className="stack gcal-now-card" style={{ gap: 6 }} aria-labelledby="now-h">
        <h3 id="now-h">{mine ? 'Now' : `${d.person.display_name}, now`}</h3>
        <p style={{ margin: 0 }} role="status">{d.now.headline}</p>
        {next ? (
          <div>
            <strong>Next: </strong>{hrefFor(next.ref) ? <Link to={hrefFor(next.ref)!}>{next.title}</Link> : next.title}
            <div className="small muted">{next.why}</div>
          </div>
        ) : <p className="muted small" style={{ margin: 0 }}>Nothing waiting. Plan ahead or take a break.</p>}
        <p className="small muted" style={{ margin: 0 }}>
          {d.counts.meetings} meetings · {d.counts.overdue} overdue · {d.counts.due_today} due today · {d.counts.waiting} waiting for a decision
        </p>
      </section>

      {d.all_day.length > 0 && <p className="small" style={{ margin: 0 }}><strong>All day: </strong>{d.all_day.map((e) => e.title).join(', ')}</p>}

      {d.blocks.length === 0 && d.backlog.length === 0
        ? <Empty title="A clear day">No meetings and nothing waiting for {mine ? 'you' : d.person.display_name}.</Empty>
        : (
          <ol className="gcal-blocks" aria-label={`Plan for ${dayName(date)}`}>
            {d.blocks.map((b) => (
              <li key={b.id} className={d.now.block_id === b.id ? 'current' : ''} aria-current={d.now.block_id === b.id ? 'time' : undefined}>
                <time className="small muted" dateTime={b.starts_at}>{clock(b.starts_at, tz)} – {clock(b.ends_at, tz)}</time>
                <h4>{b.event ? b.event.title : 'Free time'}</h4>
                {(b.event?.calendar === 'workspace' || b.event?.party || b.event?.deal) && (
                  <div className="row" style={{ gap: 6 }}>
                    {b.event?.calendar === 'workspace' && <Badge tone="info">Workspace</Badge>}
                    {b.event?.party && <PartyLink className={badgeClass()} id={b.event.party.id} name={b.event.party.name} />}
                    {b.event?.deal && <Link className={badgeClass()} to={`/deals/${b.event.deal.id}`}>{b.event.deal.title} · {b.event.deal.stage}</Link>}
                  </div>
                )}
                {b.event?.location && <div className="small muted">{b.event.location}</div>}
                <Actions label="Before it" items={b.prepare} />
                <Actions label="Work on" items={b.suggested} />
                <Actions label="After it" items={b.follow_up} />
              </li>
            ))}
          </ol>
        )}
      {d.backlog.length > 0 && (
        <section className="stack" style={{ gap: 4 }} aria-labelledby="backlog-h">
          <h3 id="backlog-h">Does not fit today</h3>
          <ul className="list" style={{ margin: 0, padding: 0, listStyle: 'none' }}>{d.backlog.map((x, i) => <ActionItem key={`${x.ref.id}-${i}`} a={x} />)}</ul>
        </section>
      )}
    </div>
  );
}

function NewEvent({ initial, cals, onClose, onDone }: { initial: { day: string; start: string; end: string }; cals: Cal[]; onClose: () => void; onDone: () => void }) {
  const { me } = useSession();
  const toast = useToast();
  const writable = cals.filter((c) => (c.kind === 'workspace' ? me.role !== 'member' : c.level === 'details'));
  const own = cals.find((c) => c.mine);
  const [form, setForm] = useState({ title: '', ...initial, calendar: own?.id ?? '', location: '', private: false });
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setError(null);
    if (!form.title.trim()) { setError(new Error('Give the event a title.')); return; }
    const starts = new Date(`${form.day}T${form.start}:00`);
    const ends = new Date(`${form.day}T${form.end}:00`);
    if (!(ends > starts)) { setError(new Error('The event must end after it starts.')); return; }
    setBusy(true);
    try {
      const out = await propose('calendar_event.create', {
        calendar_id: form.calendar, title: form.title.trim(), starts_at: starts.toISOString(), ends_at: ends.toISOString(),
        ...(form.location ? { location: form.location } : {}), ...(form.private ? { private: true } : {}),
      });
      toast(outcomeMessage(out, 'Event added'), outcomeTone(out));
      if (out.state !== 'rejected') onDone();
    } catch (e) { setError(e); } finally { setBusy(false); }
  };
  const field = (k: keyof typeof form) => ({ value: form[k] as string, onChange: (e: { target: { value: string } }) => setForm({ ...form, [k]: e.target.value }) });
  return (
    <ConfirmDialog open title="New event" confirmLabel="Add event" busy={busy} onConfirm={() => void save()} onCancel={onClose}>
      <ErrorBanner error={error} />
      <Field label="Title" required><input className={fieldClass} autoFocus {...field('title')} /></Field>
      <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
        <Field label="Day" required><input className={fieldClass} type="date" {...field('day')} /></Field>
        <Field label="Starts" required><input className={fieldClass} type="time" {...field('start')} /></Field>
        <Field label="Ends" required><input className={fieldClass} type="time" {...field('end')} /></Field>
      </div>
      <Field label="Calendar">
        <select className={selectClass} {...field('calendar')}>
          {writable.map((c) => <option key={c.id} value={c.id}>{c.kind === 'workspace' ? `${c.name} (everyone)` : c.mine ? 'My calendar' : c.name}</option>)}
        </select>
      </Field>
      <Field label="Where"><input className={fieldClass} {...field('location')} /></Field>
      <label className="row" style={{ gap: 6 }}><input type="checkbox" checked={form.private} onChange={(e) => setForm({ ...form, private: e.target.checked })} />Private: others see only that you are busy</label>
    </ConfirmDialog>
  );
}
